/**
 * Unique wrap per log format kind. Call sites must use wrapLog / wrapLogDesc / …
 * never the id strings. Keep in sync with API `routes/controllers/logFormat.ts`
 * and seeder `scripts/lib/logFormat.js`.
 *
 * desc is free-text only (`4b8KDR9`). Other kinds have their own ids.
 */

export const LOG_WRAP_ID = {
  desc: '4b8KDR9',
  bold: '7mQ2nWx',
  room: 'kP9vL3s',
  price: '2Hf8tYq',
  customer: '9cR4wNm',
  ota: '5jE6bZu',
  activity: '3aT7xKp',
  restored: '8dW1gHs',
  booking: '6nY4mFq',
  arrow: '1vC5pLr',
  actor: 'xS8hJt4',
  dept: 'qB2zVw7',
  status: '9fM3cXn',
  /** Work-order / form photo ids (comma-separated UUIDs from photos table). */
  photo: '2pH9oTm',
} as const

export type LogWrapKind = keyof typeof LOG_WRAP_ID

export const LOG_WRAP_KINDS = Object.keys(LOG_WRAP_ID) as LogWrapKind[]

export const logWrapMark = (kind: LogWrapKind): string => `&${LOG_WRAP_ID[kind]}&`

export const LOG_WRAP_MARK: Record<LogWrapKind, string> = LOG_WRAP_KINDS.reduce((acc, kind) => {
  acc[kind] = logWrapMark(kind)
  return acc
}, {} as Record<LogWrapKind, string>)

/** Legacy pair marks — restore of pre-rewrite rows. Longest first. */
export const LEGACY_PAIR_MARKS = [
  '&P&', '&V&', '&-&', '&C&', '&A&', '&L&', '&R&', '&G&',
  '|', '!', '^', '&',
] as const

export type LegacyPairMark = typeof LEGACY_PAIR_MARKS[number]

export const LEGACY_MARK_KIND: Record<LegacyPairMark, LogWrapKind> = {
  '&P&': 'price',
  '&V&': 'room',
  '&-&': 'booking',
  '&C&': 'ota',
  '&A&': 'arrow',
  '&L&': 'activity',
  '&R&': 'restored',
  '&G&': 'customer',
  '|': 'actor',
  '!': 'dept',
  '^': 'status',
  '&': 'bold',
}

/**
 * Strip only real wrap-mark tokens from free-text (new ids + multi-char legacy).
 * Do NOT strip single-char `|` `!` `^` `&` — those are fine inside wraps, and
 * stripping them broke message matching (search / rename) and burned CPU on login.
 */
const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const LOG_FORMAT_MARK_STRIP_RE = new RegExp(
  [
    ...LOG_WRAP_KINDS.map((kind) => escapeRegExp(LOG_WRAP_MARK[kind])),
    // Multi-char legacy only (`&P&` …). Single-char marks stay in user text.
    ...LEGACY_PAIR_MARKS.filter((mark) => mark.length > 1).map((mark) => escapeRegExp(mark)),
  ].join('|'),
  'g',
)

export const stripLogFormatFromUserInput = (
  value: string | number | null | undefined,
): string => String(value ?? '').replace(LOG_FORMAT_MARK_STRIP_RE, '')

/** Wrap without stripping — use when matching tokens already stored in messages. */
export const wrapLogRaw = (
  kind: LogWrapKind,
  text: string | number | null | undefined,
): string => {
  const mark = LOG_WRAP_MARK[kind]
  return `${mark}${text ?? ''}${mark}`
}

/** Wrap inner text for new log writes. Empty string still wraps so parsers see a pair. */
export const wrapLog = (kind: LogWrapKind, text: string | number | null | undefined): string => {
  // Strip format marks from inner text so user input cannot collide with wraps.
  return wrapLogRaw(kind, stripLogFormatFromUserInput(text))
}

export const wrapLogDesc = (text: string | number | null | undefined) => wrapLog('desc', text)
export const wrapLogBold = (text: string | number | null | undefined) => wrapLog('bold', text)
export const wrapLogRoom = (text: string | number | null | undefined) => wrapLog('room', text)
export const wrapLogPrice = (text: string | number | null | undefined) => wrapLog('price', text)
export const wrapLogCustomerMark = (text: string | number | null | undefined) => wrapLog('customer', text)
export const wrapLogOta = (text: string | number | null | undefined) => wrapLog('ota', text)
export const wrapLogActivity = (text: string | number | null | undefined) => wrapLog('activity', text)
export const wrapLogRestored = (text: string | number | null | undefined) => wrapLog('restored', text)
export const wrapLogBooking = (text: string | number | null | undefined) => wrapLog('booking', text)
export const wrapLogArrow = (text: string | number | null | undefined = '→') => wrapLog('arrow', text)
export const wrapLogActorMark = (text: string | number | null | undefined) => wrapLog('actor', text)
export const wrapLogDept = (text: string | number | null | undefined) => wrapLog('dept', text)
export const wrapLogStatus = (text: string | number | null | undefined) => wrapLog('status', text)
export const wrapLogPhoto = (text: string | number | null | undefined) => wrapLog('photo', text)

/** Pref / refund chip: `[bold(label)]`. */
export const wrapLogPrefTag = (label: string): string => `[${wrapLogBold(label)}]`

/** Split track_photo / wrapLogPhoto inner — comma-separated photos-table ids. */
export const parseLogPhotoIds = (raw: string | number | null | undefined): string[] => (
  String(raw ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part && !part.startsWith('data:image/'))
)

export const unwrapLog = (kind: LogWrapKind, message: string): string[] => {
  const mark = LOG_WRAP_MARK[kind]
  const out: string[] = []
  let from = 0
  const s = String(message || '')
  while (from < s.length) {
    const open = s.indexOf(mark, from)
    if (open < 0) break
    const innerStart = open + mark.length
    const close = s.indexOf(mark, innerStart)
    if (close < 0) break
    out.push(s.slice(innerStart, close))
    from = close + mark.length
  }
  return out
}

export type LogMessageSegment =
  | { type: 'wrap', kind: LogWrapKind, inner: string }
  | { type: 'legacy', mark: LegacyPairMark, kind: LogWrapKind, inner: string }
  | { type: 'text', text: string }

const NEW_MARK_ENTRIES = LOG_WRAP_KINDS.map((kind) => ({ kind, mark: LOG_WRAP_MARK[kind] }))

const markAt = (s: string, i: number):
  | { type: 'wrap', kind: LogWrapKind, mark: string }
  | { type: 'legacy', mark: LegacyPairMark }
  | null => {
  for (let m = 0; m < NEW_MARK_ENTRIES.length; m += 1) {
    const entry = NEW_MARK_ENTRIES[m]
    if (s.startsWith(entry.mark, i)) return { type: 'wrap', kind: entry.kind, mark: entry.mark }
  }
  for (let m = 0; m < LEGACY_PAIR_MARKS.length; m += 1) {
    const mark = LEGACY_PAIR_MARKS[m]
    if (s.startsWith(mark, i)) return { type: 'legacy', mark }
  }
  return null
}

/** Walk message into wrap / legacy / plain segments. Inner wrap text is opaque. */
export const parseLogMessageSegments = (message: string): LogMessageSegment[] => {
  const s = String(message || '')
  const segments: LogMessageSegment[] = []
  let i = 0
  let textStart = 0
  const flushText = (end: number) => {
    if (end > textStart) segments.push({ type: 'text', text: s.slice(textStart, end) })
  }
  while (i < s.length) {
    const hit = markAt(s, i)
    if (!hit) {
      i += 1
      continue
    }
    const mark = hit.type === 'wrap' ? hit.mark : hit.mark
    const innerStart = i + mark.length
    const close = s.indexOf(mark, innerStart)
    if (close < 0) {
      i += 1
      continue
    }
    flushText(i)
    const inner = s.slice(innerStart, close)
    if (hit.type === 'wrap') {
      segments.push({ type: 'wrap', kind: hit.kind, inner })
    } else {
      segments.push({
        type: 'legacy',
        mark: hit.mark,
        kind: LEGACY_MARK_KIND[hit.mark],
        inner,
      })
    }
    i = close + mark.length
    textStart = i
  }
  flushText(s.length)
  return segments
}

export type PlainLogMessageOpts = {
  // eslint-disable-next-line no-unused-vars
  resolveActor?: (token: string) => string
  // eslint-disable-next-line no-unused-vars
  resolveCustomer?: (token: string) => string
  // eslint-disable-next-line no-unused-vars
  formatPrice?: (inner: string) => string
}

const plainInnerForKind = (
  kind: LogWrapKind,
  inner: string,
  opts?: PlainLogMessageOpts,
): string => {
  if (kind === 'actor') return opts?.resolveActor?.(inner) ?? inner
  if (kind === 'customer') return opts?.resolveCustomer?.(inner) ?? inner
  if (kind === 'price') return opts?.formatPrice?.(inner) ?? inner
  if (kind === 'activity') return 'log'
  if (kind === 'restored') return 'restored'
  if (kind === 'booking') return ''
  if (kind === 'arrow') return ''
  if (kind === 'photo') {
    const n = parseLogPhotoIds(inner).length
    if (n <= 0) return 'photo'
    return n === 1 ? 'photo' : `${n} photos`
  }
  return inner
}

/** Names / labels only — no wrap marks. Used by search, PDFs, deleted preview. */
export const toPlainLogMessage = (raw: string, opts?: PlainLogMessageOpts): string => {
  const parts = parseLogMessageSegments(raw)
  let out = ''
  for (let p = 0; p < parts.length; p += 1) {
    const seg = parts[p]
    if (seg.type === 'text') {
      out += seg.text
      continue
    }
    out += plainInnerForKind(seg.kind, seg.inner, opts)
  }
  return out
    .replace(/→/g, '')
    .replace(/[!^#_|&]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Strip wrap marks, keep inner text (search haystack). */
export const stripLogSearchMarks = (value: string): string => {
  const parts = parseLogMessageSegments(value)
  let out = ''
  for (let p = 0; p < parts.length; p += 1) {
    const seg = parts[p]
    if (seg.type === 'text') {
      out += seg.text
      continue
    }
    if (seg.kind === 'booking' || seg.kind === 'arrow') continue
    out += seg.inner
  }
  return out.replace(/[&|!^#_]/g, '')
}

export const unwrapLogWithLegacy = (kind: LogWrapKind, message: string): string[] => {
  const parts = parseLogMessageSegments(message)
  const out: string[] = []
  for (let p = 0; p < parts.length; p += 1) {
    const seg = parts[p]
    if (seg.type !== 'text' && seg.kind === kind) out.push(seg.inner)
  }
  return out
}

export const messageHasWrapped = (
  message: string,
  kind: LogWrapKind,
  inner: string,
): boolean => {
  if (!message || inner == null || inner === '') return false
  // Raw wrap: match what is actually stored (do not re-strip the inner token).
  if (message.includes(wrapLogRaw(kind, inner))) return true
  const found = unwrapLogWithLegacy(kind, message)
  return found.some((v) => v === String(inner))
}
